import { axiosApiInstancePrivate } from '../index';
import axios, { AxiosError } from 'axios';
import { Task, Lead } from '@tassomutuo-backoffice/types';
import { appendHsOwnerIdIfPresentAndAdmin } from '../../../utils/apiUtilis';

export interface GetTasksParams {
  leadId?: string;
  completed?: boolean;
  dateFrom?: string; // ISO date string
  dateTo?: string; // ISO date string
  sortBy?: string; // format: field:asc/desc (e.g., "date:asc")
  limit?: number;
  page?: number;
}

export interface TasksResponse {
  results: Task[];
  page: number;
  limit: number;
  totalPages: number;
  totalResults: number;
}

export interface CalendarTasksResponse {
  [date: string]: Task[];
}

export interface GetCalendarTasksParams {
  startDate?: string; // ISO date string
  endDate?: string; // ISO date string
}

/**
 * Get all tasks with filters and pagination
 * @param params Query parameters for filtering and pagination
 * @returns Paginated task results
 */
export async function getTasks(
  params?: GetTasksParams
): Promise<TasksResponse> {
  try {
    let url = '/v1/tasks';

    if (params) {
      const queryParams = new URLSearchParams();

      if (params.leadId) queryParams.append('leadId', params.leadId);
      if (params.completed !== undefined)
        queryParams.append('completed', String(params.completed));
      if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
      if (params.dateTo) queryParams.append('dateTo', params.dateTo);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.limit) queryParams.append('limit', String(params.limit));
      if (params.page) queryParams.append('page', String(params.page));

      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    // Add hsOwnerId if present and user is admin or has team
    url = appendHsOwnerIdIfPresentAndAdmin(url);

    const response = await axiosApiInstancePrivate.get<TasksResponse>(url);

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
}

/**
 * Get tasks grouped by date for calendar view
 * @param params Optional start and end date for the calendar range
 * @returns Tasks grouped by date
 */
export async function getCalendarTasks(
  params?: GetCalendarTasksParams
): Promise<CalendarTasksResponse> {
  try {
    let url = '/v1/tasks/calendar';

    if (params) {
      const queryParams = new URLSearchParams();

      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);

      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    // Add hsOwnerId if present and user is admin or has team
    url = appendHsOwnerIdIfPresentAndAdmin(url);

    const response = await axiosApiInstancePrivate.get<CalendarTasksResponse>(
      url
    );

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
}

export interface UpdateTaskParams {
  date?: string; // ISO date string
  action?: string;
  notes?: string;
  completed?: boolean;
  lead?: Lead;
}

/**
 * Update a task
 * @param taskId Task ID to update
 * @param updateData Data to update
 * @returns Updated task
 */
export async function updateTask(
  taskId: string,
  updateData: UpdateTaskParams
): Promise<Task> {
  try {
    const url = appendHsOwnerIdIfPresentAndAdmin(`/v1/tasks/${taskId}`);
    const response = await axiosApiInstancePrivate.patch<Task>(
      url,
      updateData
    );

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
}

/**
 * Delete a task
 * @param taskId Task ID to delete
 */
export async function deleteTask(taskId: string): Promise<void> {
  try {
    const url = appendHsOwnerIdIfPresentAndAdmin(`/v1/tasks/${taskId}`);
    await axiosApiInstancePrivate.delete(url);
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
}

/**
 * Create a new task
 * @param taskData Task data to create
 * @returns Created task
 */
export async function createTask(taskData: {
  lead: Lead;
  date: string;
  action: string;
  notes?: string;
}): Promise<Task> {
  try {
    const url = appendHsOwnerIdIfPresentAndAdmin('/v1/tasks');
    const response = await axiosApiInstancePrivate.post<Task>(
      url,
      taskData
    );

    return response.data;
  } catch (err) {
    const error = err as Error | AxiosError;
    if (axios.isAxiosError(error)) {
      throw error?.response?.data;
    }
    throw err;
  }
}
